import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  DirectionEnum,
  hubSettingsDirectionsOverlap,
  isHubSettingsDirection,
  isValidPositiveNumber,
  yupLocalized,
} from "@ryde/shared";

import { IStationHubItem } from "./table-station-hub.models";

export const useStationHubItemResolver = (
  existingItems: IStationHubItem[] = [],
  currentStationId?: number,
) => {
  const { t } = useTranslation();

  const stationHubItemFormValidationSchema = useMemo(() => {
    return yupLocalized
      .object()
      .shape({
        destinationStationId: yupLocalized.number().required(),
        direction: yupLocalized
          .string()
          .oneOf([
            DirectionEnum.PickUp,
            DirectionEnum.DropOff,
            DirectionEnum.Both,
          ])
          .required(),
        hubId: yupLocalized
          .number()
          .required()
          .test(
            "hub-not-same-as-stations",
            t("hub.hubSameAsPairedStation"),
            (hubId, context) => {
              const destinationStationId = context.parent
                ?.destinationStationId as number | undefined;
              if (!isValidPositiveNumber(hubId)) {
                return true;
              }
              if (hubId === destinationStationId) {
                return false;
              }
              if (
                isValidPositiveNumber(currentStationId) &&
                hubId === currentStationId
              ) {
                return false;
              }
              return true;
            },
          ),
      })
      .test(
        "hub-destination-direction-unique",
        t("hub.duplicatePairedStation"),
        value => {
          const destinationStationId = value?.destinationStationId as
            number | undefined;
          const direction = value?.direction;
          if (
            !isValidPositiveNumber(destinationStationId) ||
            !isHubSettingsDirection(direction)
          ) {
            return true;
          }

          return !existingItems.some(
            item =>
              item.destinationStationId === destinationStationId &&
              hubSettingsDirectionsOverlap({
                left: item.direction,
                right: direction,
              }),
          );
        },
      );
  }, [existingItems, currentStationId, t]);

  return { stationHubItemFormValidationSchema };
};
